import { ReactNode } from 'react';

import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useSelector } from 'react-redux';

import {
  Carousel,
  CarouselNav,
  TURN_ROW_SLIDE_SPACING,
  useCarouselNav,
} from '@/components/organisms/Dialogs/Carousel/Carousel';
import { cn } from '@/lib/utils';
import { selectIsReplay } from '@/modules/tryIt/redux/selectors';
import { ResponseMessage } from '@/shared/types/tryIt';

import { ResponseCard } from './ResponseNodeLabel';

type CarouselMessage = Extract<ResponseMessage, { type: 'carousel' }>;

interface CarouselResponseProps {
  response: CarouselMessage;
  onPostback: (text: string) => void;
  icon: ReactNode;
  goToButton: ReactNode;
  turnRow?: boolean;
  onGoToNode?: () => void;
  nodeHref?: string;
  nodeType?: string;
}

const ARROW = 'flex items-center justify-center rounded p-0.5 text-muted-foreground transition';
const ARROW_ENABLED = 'hover:bg-selected-soft';
const ARROW_DISABLED = 'opacity-40';

const CarouselArrows = ({ nav }: { nav: CarouselNav }) => (
  <>
    <button
      type="button"
      disabled={!nav.canGoPrev}
      onClick={nav.prev}
      className={cn(ARROW, nav.canGoPrev ? ARROW_ENABLED : ARROW_DISABLED)}
    >
      <ArrowLeftIcon size={16} />
    </button>
    <button
      type="button"
      disabled={!nav.canGoNext}
      onClick={nav.next}
      className={cn(ARROW, nav.canGoNext ? ARROW_ENABLED : ARROW_DISABLED)}
    >
      <ArrowRightIcon size={16} />
    </button>
  </>
);

export const CarouselResponse = ({
  response,
  onPostback,
  icon,
  goToButton,
  turnRow = false,
  onGoToNode,
  nodeHref,
  nodeType = 'carousel',
}: CarouselResponseProps) => {
  const isReplay = useSelector(selectIsReplay);
  const nav = useCarouselNav(response.cards?.length ?? 0, TURN_ROW_SLIDE_SPACING);

  if (turnRow) {
    const hasArrows = (response.cards?.length ?? 0) > 1;
    return (
      <ResponseCard
        type={nodeType}
        onGoToNode={onGoToNode}
        nodeHref={nodeHref}
        actions={hasArrows ? <CarouselArrows nav={nav} /> : undefined}
      >
        <Carousel
          cards={response.cards}
          onPostback={onPostback}
          isReplay={isReplay}
          nav={nav}
          turnRow
        />
      </ResponseCard>
    );
  }

  return (
    <div className="response flex w-full items-start gap-3 [&:not(:last-child)]:mb-2">
      {icon}
      <Carousel cards={response.cards} onPostback={onPostback} isReplay={isReplay} />
      {goToButton && <div className="self-center">{goToButton}</div>}
    </div>
  );
};
